// components/SubscriptionModal.tsx
'use client';

import { useState, useEffect } from 'react';
import { SubscriptionService } from '../../services/subscriptionService';
import { supabase } from '../../services/supabase';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCredits: number;
}

interface Plan {
  id: string;
  name: string;
  display_name: string;
  price: number;
  credits: number;
  description: string;
}

export default function SubscriptionModal({ isOpen, onClose, currentCredits }: SubscriptionModalProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
    }
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      const data = await SubscriptionService.getPlans();
      setPlans(data);
      if (data.length > 0) {
        setSelectedPlan(data[1]?.id); // Select Basic plan by default
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const handlePurchase = async () => {
    if (!selectedPlan) return;
    
    setLoading(true);
    try {
      // Get user info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', user.id)
        .single();

      const result = await SubscriptionService.initializePayment(
        user.id,
        selectedPlan,
        profile?.email || user.email || '',
        profile?.full_name || 'User',
        'USD'
      );

      // Redirect to Flutterwave
      window.location.href = result.checkoutUrl;
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Upgrade Your Plan</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="current-balance">
          <p>Current Credits: <strong>{currentCredits}</strong></p>
        </div>

        <div className="plans-grid">
          {plans.filter(p => p.name !== 'free').map((plan) => (
            <div 
              key={plan.id}
              className={`plan-card ${selectedPlan === plan.id ? 'selected' : ''}`}
              onClick={() => setSelectedPlan(plan.id)}
            >
              <h3>{plan.display_name}</h3>
              <div className="plan-price">${plan.price.toFixed(2)}</div>
              <div className="plan-credits">{plan.credits} credits</div>
              <p className="plan-description">{plan.description}</p>
              <div className="plan-features">
                <div>✓ {plan.credits} credits immediately</div>
                <div>✓ All exam types</div>
                <div>✓ Priority support</div>
              </div>
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-btn">
            Cancel
          </button>
          <button 
            onClick={handlePurchase} 
            className="purchase-btn"
            disabled={loading || !selectedPlan}
          >
            {loading ? 'Processing...' : 'Continue to Payment'}
          </button>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 30px;
          width: 90%;
          max-width: 1000px;
          max-height: 90vh;
          overflow-y: auto;
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        
        .close-btn {
          background: none;
          border: none;
          font-size: 28px;
          cursor: pointer;
          color: #666;
        }
        
        .current-balance {
          background: #f0f9ff;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 25px;
          text-align: center;
        }
        
        .plans-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }
        
        .plan-card {
          border: 2px solid #e0e0e0;
          border-radius: 10px;
          padding: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        .plan-card.selected {
          border-color: #4f46e5;
          background: #f8f9ff;
          transform: translateY(-5px);
        }
        
        .plan-card h3 {
          margin: 0 0 10px 0;
          color: #333;
        }
        
        .plan-price {
          font-size: 32px;
          font-weight: bold;
          color: #4f46e5;
          margin-bottom: 5px;
        }
        
        .plan-credits {
          color: #666;
          margin-bottom: 15px;
        }
        
        .plan-description {
          color: #777;
          font-size: 14px;
          margin-bottom: 15px;
          min-height: 40px;
        }
        
        .plan-features div {
          color: #555;
          font-size: 14px;
          margin-bottom: 8px;
        }
        
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 15px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }
        
        .cancel-btn, .purchase-btn {
          padding: 12px 30px;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          border: none;
        }
        
        .cancel-btn {
          background: #f1f1f1;
          color: #666;
        }
        
        .purchase-btn {
          background: #4f46e5;
          color: white;
        }
        
        .purchase-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}